import React, { useState, useEffect } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { getAllVotes, deleteVote } from "./../../../services/voteServices";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import EditVoteModal from "./EditVoteModal";
import AddVoteModal from "./AddVoteModal";
import VoteQuestionsModal from "./VoteQuestionsModal";
import VoteResultsModal from "./VoteResultsModal";

const VotePage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const [voteData, setVoteData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentVote, setCurrentVote] = useState(null);
  const [openQuestionsModal, setOpenQuestionsModal] = useState(false);
  const [openResultsModal, setOpenResultsModal] = useState(false);
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [selectedVoteTitle, setSelectedVoteTitle] = useState("");

  const fetchVotes = async () => {
    try {
      const data = await getAllVotes();
      if (Array.isArray(data)) {
        setVoteData(data);
      } else {
        throw new Error("Données invalides");
      }
    } catch (error) {
      console.error("Erreur chargement des votes :", error);
      toast.error("Erreur lors de la récupération des votes");
    }
  };

  useEffect(() => {
    fetchVotes();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEditClick = (vote) => {
    setCurrentVote(vote);
    setShowEditModal(true);
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleDeleteClick = async (id) => {
    try {
      await deleteVote(id);
      setVoteData((prev) => prev.filter((v) => v.id !== id));
      toast.success("Vote supprimé avec succès");
    } catch (error) {
      console.error("Erreur suppression vote :", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleOpenQuestionsModal = (voteId, voteTitle) => {
    setSelectedVoteId(voteId);
    setSelectedVoteTitle(voteTitle);
    setOpenQuestionsModal(true);
  };

  const handleCloseQuestionsModal = () => {
    setOpenQuestionsModal(false);
    setSelectedVoteId(null);
    setSelectedVoteTitle("");
  };

  const filteredVotes = voteData.filter((v) =>
    `${v.title} ${v.description}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleOpenResultsModal = (voteId, voteTitle) => {
    setSelectedVoteId(voteId);
    setSelectedVoteTitle(voteTitle);
    setOpenResultsModal(true);
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <h2 className="text-2xl font-light tracking-[0.3em] text-center mb-2">
        ADMINISTRATEUR
      </h2>
      <h2 className="text-3xl font-bold uppercase tracking-widest text-center mb-4 font-montserrat">
        Gestion des Votes
      </h2>
      <DashboardNavigation role={userRole} />

      <div className="flex flex-col items-center w-full px-4 mt-4 max-w-[1280px] mx-auto">
        <input
          type="text"
          placeholder="Rechercher par titre ou description"
          value={searchTerm}
          onChange={handleSearchChange}
          className="mb-4 p-2 border border-gray-300 rounded w-full sm:w-1/2 dark:bg-gray-800 dark:text-white"
        />

        <Button
          text="Ajouter un Vote / Sondage"
          onClick={handleAddClick}
          className="mb-4"
        />

        <div className="bg-gray-100 dark:bg-gray-500 shadow-lg w-full font-montserrat mb-10">
          <div className="overflow-x-auto">
            <table className="table-auto bg-white dark:bg-slate-300 w-full border-collapse border border-slate-300">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-3 border text-xs uppercase text-white">
                    Titre
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Description
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Type
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Période
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Statut
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Questions
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Nb Votants
                  </th>
                  <th className="p-3 border text-xs uppercase text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredVotes.map((vote) => (
                  <tr
                    key={vote.id}
                    className="text-sm dark:text-black hover:bg-gray-100 dark:hover:bg-gray-400"
                  >
                    <td className="p-2 border">{vote.title}</td>
                    <td className="p-2 border">{vote.description}</td>
                    <td className="p-2 border capitalize">{vote.type}</td>
                    <td className="p-2 border">
                      {vote.start_time} → {vote.end_time}
                    </td>
                    <td className="p-2 border">
                      {vote.is_active ? "Actif" : "Inactif"}
                    </td>
                    <td className="p-2 border text-center">
                      {vote.questionCount || 0}
                    </td>
                    <td className="p-2 border text-center">
                      {vote.voterCount || 0}
                    </td>

                    <td className="p-2 border flex justify-center gap-2 flex-wrap">
                      <ButtonEdit onClick={() => handleEditClick(vote)} />
                      <ButtonDelete
                        onClick={() => handleDeleteClick(vote.id)}
                      />
                      <Button
                        text="Questions"
                        className="btn-secondary text-xs"
                        onClick={() =>
                          handleOpenQuestionsModal(vote.id, vote.title)
                        }
                      />
                      <Button
                        text="Résultats"
                        className="text-xs"
                        onClick={() =>
                          handleOpenResultsModal(vote.id, vote.title)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer />

      {showEditModal && (
        <EditVoteModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          vote={currentVote}
          refreshList={fetchVotes}
        />
      )}

      {showAddModal && (
        <AddVoteModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          refreshList={fetchVotes}
        />
      )}

      {openQuestionsModal && (
        <VoteQuestionsModal
          isOpen={openQuestionsModal}
          onClose={handleCloseQuestionsModal}
          voteId={selectedVoteId}
          voteTitle={selectedVoteTitle}
        />
      )}

      {openResultsModal && (
        <VoteResultsModal
          isOpen={openResultsModal}
          onClose={() => setOpenResultsModal(false)}
          voteId={selectedVoteId}
          voteTitle={selectedVoteTitle}
        />
      )}
    </div>
  );
};

export default VotePage;
